import React, { useState, useEffect, useCallback } from 'react';
import { pricingAPI } from '../../../services/api';
import { CurrencyDollarIcon, MagnifyingGlassIcon, ArrowPathIcon, CheckIcon } from '@heroicons/react/24/outline';
import Pagination from '../../../components/common/Pagination';

const CURRENCIES = [
  { code: 'INR', label: 'INR - Indian Rupee', symbol: '₹' },
  { code: 'USD', label: 'USD - US Dollar', symbol: '$' },
  { code: 'EUR', label: 'EUR - Euro', symbol: '€' },
  { code: 'GBP', label: 'GBP - British Pound', symbol: '£' },
  { code: 'AED', label: 'AED - UAE Dirham', symbol: 'د.إ' },
];

const getCurrencySymbol = (code) => CURRENCIES.find(c => c.code === code)?.symbol || code;

const RateManagement = () => {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Inline edit state
  const [editingId, setEditingId] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [editCurrency, setEditCurrency] = useState('INR');

  // Bulk update state
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkPrice, setBulkPrice] = useState('');
  const [bulkCurrency, setBulkCurrency] = useState('INR');

  const fetchData = useCallback(async (page = currentPage, size = pageSize) => {
    try {
      setLoading(true);
      setError('');
      const [pricingRes, summaryRes] = await Promise.all([
        pricingAPI.getAll(page, size, search),
        pricingAPI.getSummary()
      ]);

      const data = pricingRes.data?.data || [];
      const total = pricingRes.data?.total || 0;
      setRecords(data);
      setTotalRecords(total);
      setTotalPages(Math.ceil(total / size));
      setSummary(summaryRes.data);
    } catch (err) {
      setError('Failed to fetch pricing data');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const handleInlineEdit = (record) => {
    setEditingId(record.id);
    setEditPrice(record.price_per_sms.toString());
    setEditCurrency(record.currency);
  };

  const handleInlineSave = async (record) => {
    try {
      await pricingAPI.update({
        user_id: record.user_id,
        price_per_sms: parseFloat(editPrice),
        currency: editCurrency,
        route_type: record.route_type || 'default'
      });
      setSuccess(`Rate updated for ${record.username}`);
      setEditingId(null);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update rate');
    }
  };

  const handleInlineCancel = () => {
    setEditingId(null);
    setEditPrice('');
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === records.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(records.map(r => r.user_id));
    }
  };

  const handleBulkUpdate = async () => {
    if (!bulkPrice || selectedIds.length === 0) return;
    try {
      const res = await pricingAPI.bulkUpdate({
        user_ids: selectedIds,
        price_per_sms: parseFloat(bulkPrice),
        currency: bulkCurrency,
        route_type: 'default'
      });
      setSuccess(res.data?.message || 'Bulk update complete');
      setShowBulkModal(false);
      setSelectedIds([]);
      setBulkPrice('');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Bulk update failed');
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Users with Pricing</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{summary.users_with_pricing}</div>
            <div className="text-xs text-gray-400">of {summary.total_users} total</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Avg Rate</div>
            <div className="text-2xl font-bold text-amber-600 mt-1">{getCurrencySymbol(summary.default_currency)}{parseFloat(summary.avg_price_per_sms).toFixed(4)}</div>
            <div className="text-xs text-gray-400">per SMS</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Min Rate</div>
            <div className="text-2xl font-bold text-green-600 mt-1">{getCurrencySymbol(summary.default_currency)}{parseFloat(summary.min_price_per_sms).toFixed(4)}</div>
            <div className="text-xs text-gray-400">per SMS</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Max Rate</div>
            <div className="text-2xl font-bold text-red-600 mt-1">{getCurrencySymbol(summary.default_currency)}{parseFloat(summary.max_price_per_sms).toFixed(4)}</div>
            <div className="text-xs text-gray-400">per SMS</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Total Balance</div>
            <div className="text-2xl font-bold text-indigo-600 mt-1">{getCurrencySymbol(summary.default_currency)}{parseFloat(summary.total_balance).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</div>
            <div className="text-xs text-gray-400">~{summary.total_sms_capacity?.toLocaleString()} SMS capacity</div>
          </div>
        </div>
      )}

      {/* Rate Distribution Chart (simple bar visualization) */}
      {summary && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Rate Distribution</h3>
          <div className="flex items-end gap-1 h-16">
            {records.slice(0, 30).map((r, i) => {
              const maxRate = summary.max_price_per_sms || 1;
              const height = Math.max(4, (r.price_per_sms / maxRate) * 100);
              return (
                <div key={i} className="flex-1 group relative">
                  <div
                    className="bg-amber-400 hover:bg-amber-500 rounded-t transition-colors cursor-pointer"
                    style={{ height: `${height}%` }}
                    title={`${r.username}: ${r.currency} ${r.price_per_sms}`}
                  />
                  <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-1 bg-gray-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10">
                    {r.username}: {r.currency} {parseFloat(r.price_per_sms).toFixed(4)}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-[10px] text-gray-400 mt-1">
            <span>Min: {getCurrencySymbol(summary.default_currency)}{parseFloat(summary.min_price_per_sms).toFixed(4)}</span>
            <span>Max: {getCurrencySymbol(summary.default_currency)}{parseFloat(summary.max_price_per_sms).toFixed(4)}</span>
          </div>
        </div>
      )}

      {/* Main Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <CurrencyDollarIcon className="w-5 h-5 text-amber-600" />
              Rate Management
            </h2>
            <div className="flex items-center gap-3">
              <div className="relative">
                <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search users..."
                  className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent w-56"
                />
              </div>
              {selectedIds.length > 0 && (
                <button
                  onClick={() => setShowBulkModal(true)}
                  className="px-3 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 flex items-center gap-1"
                >
                  <ArrowPathIcon className="w-4 h-4" />
                  Bulk Update ({selectedIds.length})
                </button>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="mx-4 mt-3 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">{error}</div>
        )}
        {success && (
          <div className="mx-4 mt-3 bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded-lg text-sm">{success}</div>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === records.length && records.length > 0}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 text-amber-600 border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate / SMS</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Currency</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SMS Capacity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Route</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {records.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-4 py-8 text-center text-gray-500">No pricing records found</td>
                    </tr>
                  ) : (
                    records.map((record) => (
                      <tr key={record.id} className={`hover:bg-gray-50 ${selectedIds.includes(record.user_id) ? 'bg-amber-50' : ''}`}>
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(record.user_id)}
                            onChange={() => toggleSelect(record.user_id)}
                            className="h-4 w-4 text-amber-600 border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">{record.username}</div>
                          <div className="text-xs text-gray-500">{record.full_name || record.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          {editingId === record.id ? (
                            <input
                              type="number"
                              step="0.0001"
                              min="0.0001"
                              value={editPrice}
                              onChange={(e) => setEditPrice(e.target.value)}
                              className="w-28 px-2 py-1 border border-amber-300 rounded text-sm focus:ring-1 focus:ring-amber-500"
                              autoFocus
                            />
                          ) : (
                            <span className="text-sm font-semibold text-amber-700">
                              {getCurrencySymbol(record.currency)}{parseFloat(record.price_per_sms).toFixed(4)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {editingId === record.id ? (
                            <select
                              value={editCurrency}
                              onChange={(e) => setEditCurrency(e.target.value)}
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              {CURRENCIES.map(c => (
                                <option key={c.code} value={c.code}>{c.code}</option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-sm text-gray-600">{record.currency}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-900">{getCurrencySymbol(record.currency)}{parseFloat(record.balance).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-indigo-600">{record.sms_capacity?.toLocaleString() || 0}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{record.route_type}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${record.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {record.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {editingId === record.id ? (
                            <div className="flex gap-1">
                              <button onClick={() => handleInlineSave(record)} className="p-1 text-green-600 hover:bg-green-50 rounded" title="Save">
                                <CheckIcon className="w-4 h-4" />
                              </button>
                              <button onClick={handleInlineCancel} className="p-1 text-gray-400 hover:bg-gray-50 rounded text-sm" title="Cancel">
                                ✕
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleInlineEdit(record)}
                              className="text-xs text-amber-600 hover:text-amber-800 font-medium"
                            >
                              Edit
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t border-gray-200">
              <Pagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalRecords={totalRecords}
                totalPages={totalPages}
                onPageChange={(p) => { setCurrentPage(p); fetchData(p, pageSize); }}
                onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); fetchData(1, s); }}
              />
            </div>
          </>
        )}
      </div>

      {/* Bulk Update Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowBulkModal(false)}></div>
            <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="px-6 pt-5 pb-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Bulk Update Rates</h3>
                <p className="text-sm text-gray-500 mt-1">{selectedIds.length} user(s) selected</p>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">New Price per SMS *</label>
                  <input
                    type="number"
                    step="0.0001"
                    min="0.0001"
                    value={bulkPrice}
                    onChange={(e) => setBulkPrice(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="e.g. 0.2500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Currency</label>
                  <select
                    value={bulkCurrency}
                    onChange={(e) => setBulkCurrency(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  >
                    {CURRENCIES.map(c => (
                      <option key={c.code} value={c.code}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-3 flex justify-end gap-3 border-t border-gray-200">
                <button onClick={() => setShowBulkModal(false)} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleBulkUpdate} disabled={!bulkPrice} className="px-4 py-2 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 disabled:opacity-50">
                  Update {selectedIds.length} Users
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RateManagement;
